import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useRef } from 'react';
import $ from 'jquery';
import { clearAllBodyScrollLocks, disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';

import ModalContainer from '../common/modal-container/modal-container';
import { hideModal } from '../../store/slices/generalSlice';
import { RootState } from '../../store/rootReducer';
import { logoChat } from '../../assets/images/images';
import { chatIcons, icons } from '../../assets/icons/icons';
import {
  startImbeeChat,
  chatbotInitialAnimation,
  handleChatOpening,
  setGlobalChatVariables,
  ImbeeChatVariables,
  setGlobalChatIcon,
  setGlobalChatBgCol,
} from './imbee-chat-utils';
import { companyChatProps, ImbeeChatStyles } from './imbee-chat-styles';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
declare const window: Window &
  typeof globalThis & {
    imbeeShow?: any;
  };

interface IImbeeChatProps {
  onClick?: () => any;
  onClose?: () => any;
  hideCloseButton?: boolean;
  chatVariables?: ImbeeChatVariables;
}

interface ImbeeChatTheme {
  icon?: string;
  bgCol?: string;
}

interface ImbeeChatStyleProps {
  theme?: ImbeeChatTheme;
}

const ImbeeChatStyleShow = styled(ImbeeChatStyles)<ImbeeChatStyleProps>`
  .c-chatbot:not(.c-chatbot--opened) .c-chatbot__head .c-chatbot__head__bg-icon {
    display: block;
    width: 30px;
    height: 30px;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    pointer-events: none;
  }
  .c-chatbot:not(.c-chatbot--opened) .c-chatbot__head .c-chatbot__head__close-icon {
    display: none;
  }
  .c-chatbot.c-chatbot--opened .c-chatbot__head .c-chatbot__head__bg-icon {
    display: none;
  }
  .c-chatbot.c-chatbot--opened .c-chatbot__head .c-chatbot__head__close-icon {
    display: block;
    width: 28px;
    height: 28px;
    position: absolute;
    top: 10px;
    right: 10px;
    pointer-events: none;
  }
  .c-chatbot.c-chatbot--opened .c-chatbot__head {
    background-color: ${(props) => props.theme?.bgCol || '#0094D9'};
  }
  .modal-top {
    background-color: ${(props) => props.theme?.bgCol || '#0094D9'};
  }
`;

const ImbeeChat: React.FC<IImbeeChatProps> = ({ onClick, onClose, hideCloseButton = false, chatVariables }) => {
  const modal = useSelector((state: RootState) => state.general.modal);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'imbee']);
  const provider = useSelector((state: RootState) => state.general.provider);

  const modalRef = useRef<HTMLDivElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const didMountRef = useRef<boolean>(false);
  const chatRef = useRef<JQuery<HTMLElement> | null>(null);
  const toggleChat = useSelector((state: RootState) => state.general.toggleChat);
  const prevToggleChatRef = useRef(toggleChat);

  const { themeChat, logoForChat } = useMemo(() => {
    if (provider) {
      return {
        themeChat: companyChatProps[provider as keyof typeof companyChatProps],
        logoForChat: logoChat[provider as keyof typeof logoChat],
      };
    }
    return {
      themeChat: companyChatProps['00000016'],
      logoForChat: logoChat['00000016'],
    };
  }, [provider]);

  // Efecto para abrir el chat al cargar el componente si el chat es modal
  useEffect(() => {
    if (modal?.name === 'imbee-chat') {
      startImbeeChat(logoForChat, 'DIV_ID', chatVariables);
      const imbeeKey = import.meta.env.VITE_IMBEE_KEY || '';
      if (imbeeKey && !document.querySelector(`script[src="${imbeeKey}"]`)) {
        const script = document.createElement('script');
        script.src = imbeeKey;
        document.head.appendChild(script);
      }
      disableBodyScroll(modalRef.current as HTMLElement);
    } else {
      enableBodyScroll(modalRef.current as HTMLElement);
    }
    return () => {
      clearAllBodyScrollLocks();
    };
  }, [modal.name, logoForChat, chatVariables]);

  const handleBubbleClick = () => {
    if (!chatRef.current?.hasClass('c-chatbot--opened') && didMountRef.current && onClick) {
      onClick();
    }
  };

  // Efecto para manejar la apertura del chat al cambiar el estado toggleChat. Chat modal.
  useEffect(() => {
    if (hideCloseButton) return;
    chatRef.current = $('.c-chatbot');
    const toggleChatChanged = prevToggleChatRef.current !== toggleChat;
    prevToggleChatRef.current = toggleChat;
    if (!toggleChatChanged) return;
    if (!chatRef.current.hasClass('c-chatbot--opened') && didMountRef.current) {
      handleChatOpening(logoForChat, chatVariables);
    }
  }, [toggleChat, logoForChat, hideCloseButton, chatVariables]);

  useEffect(() => {
    setGlobalChatVariables(chatVariables ?? []);
  }, [chatVariables]);

  useEffect(() => {
    setGlobalChatIcon(logoForChat);
  }, [logoForChat]);

  useEffect(() => {
    if (themeChat?.bgCol) {
      setGlobalChatBgCol(themeChat.bgCol);
    }
  }, [themeChat?.bgCol]);

  // Efecto para manejar la animación inicial del chat
  useEffect(() => {
    if (bubbleRef.current?.clientHeight) {
      chatbotInitialAnimation();
      didMountRef.current = true;
    }
  }, []);

  // Efecto para manejar el evento de cierre del chat
  useEffect(() => {
    if (onClose) {
      $('body').on('chatbot-minimize', '.c-chatbot', onClose);
      return () => {
        $('body').off('chatbot-minimize', '.c-chatbot', onClose);
      };
    }
  }, [onClose]);

  return (
    <ImbeeChatStyleShow theme={themeChat}>
      {!hideCloseButton && (
        <div className="c-chatbot__wrapper">
          <div ref={bubbleRef} id="c-chatbot__FULLSCREEN" className="c-chatbot c-chatbot--unread-messages animated">
            <div className="c-chatbot__head e-chatbot--toggle" onClick={handleBubbleClick}>
              <img className="c-chatbot__head__bg-icon" src={themeChat?.icon || chatIcons['00000016']} alt="" />
              <img className="c-chatbot__head__close-icon" src={icons.closeIconWhite} alt="" />
              <a className="c-chatbot__head__title">
                <span className="c-chatbot__head__icon loicon-chatbot">
                  <i className="fas fa-robot"></i>
                </span>
                {t('common:AsiturHelpsYou')}
              </a>
              <div className="c-chatbot__head__unread-messages">
                <div className="c-chatbot__head__unread-messages__dots">
                  <div></div>
                  <div></div>
                  <div></div>
                </div>
              </div>
            </div>
            <div className="c-chatbot__body">
              <div
                id="imbee"
                className="contenido"
                style={{ width: '100%', height: '100%' }}
                // src="/static/js/user4.png"
              ></div>
            </div>
          </div>
        </div>
      )}
      <ModalContainer
        fullScreen
        active={modal.name === 'imbee-chat'}
        className={hideCloseButton ? 'hide-close-button' : ''}
        required={hideCloseButton}
        onClose={() => {
          if (location.pathname.includes('confirmation/error')) {
            navigate('/');
          }
          dispatch(hideModal());
        }}
        title={t('imbee:chat-with-us')}
        text={''}
        dataCy="imbee-chat-modal"
      >
        <div ref={modalRef} id="DIV_ID"></div>
      </ModalContainer>
    </ImbeeChatStyleShow>
  );
};

export default ImbeeChat;
